import { useRef, useEffect, useCallback } from 'react';
import useArticleStore from '../stores/useArticleStore';
import { createDraft, autosaveArticle } from '../api';
import useCsrfToken from './useCsrfToken';

var DEBOUNCE_MS = 1500;

function hasTitleId(store) {
  return store.title_id && store.title_id.trim() !== '';
}

export default function useAutosave() {
  var csrfToken = useCsrfToken();
  var timerRef = useRef(null);
  var isSavingRef = useRef(false);
  var isCreatingRef = useRef(false);

  var ensureDraftExists = useCallback(function() {
    var store = useArticleStore.getState();
    if (store.id) return Promise.resolve(store.id);
    if (isCreatingRef.current) return Promise.resolve(null);

    isCreatingRef.current = true;
    return createDraft(csrfToken)
      .then(function(data) {
        if (data.success && data.article) {
          store.updateFields({
            id: data.article.id,
            slug: data.article.slug,
            autosave_token: data.article.autosave_token
          });
          return data.article.id;
        }
        return null;
      })
      .catch(function() {
        return null;
      })
      .finally(function() {
        isCreatingRef.current = false;
      });
  }, [csrfToken]);

  var save = useCallback(function() {
    var store = useArticleStore.getState();
    if (isSavingRef.current) return;
    if (!store.hasUnsavedChanges()) return;

    var changedFields = store.getChangedFields();
    if (Object.keys(changedFields).length === 0) return;

    if (!store.id && !hasTitleId(store)) return;

    isSavingRef.current = true;
    store.setSaveStatus('saving');

    var doSave = store.id ? Promise.resolve(store.id) : ensureDraftExists();

    doSave.then(function(articleId) {
      if (!articleId) {
        store.setSaveStatus('error');
        isSavingRef.current = false;
        return;
      }

      // Re-read store after draft creation (token/id may have changed)
      var currentStore = useArticleStore.getState();
      var fieldsToSave = currentStore.getChangedFields();
      if (Object.keys(fieldsToSave).length === 0) fieldsToSave = changedFields;

      return autosaveArticle(
        csrfToken,
        articleId,
        currentStore.autosave_token,
        fieldsToSave
      ).then(function(result) {
        if (result.status === 409) {
          currentStore.setSaveStatus('error');
          if (window.confirm('A newer version exists. Reload?')) {
            window.location.reload();
          }
        } else if (result.data.success) {
          var article = result.data.article;
          // Update server-generated fields before marking saved
          if (article.slug) currentStore.updateField('slug', article.slug);
          if (article.image_url !== undefined) {
            currentStore.updateField('image_url', article.image_url || null);
            currentStore.updateField('image', null);
            currentStore.updateField('remove_image', false);
          }
          if (article.has_publish_record !== undefined) {
            currentStore.updateField('has_publish_record', article.has_publish_record);
          }
          if (article.published !== undefined) {
            currentStore.updateField('published', article.published);
          }
          currentStore.markSaved(article.autosave_token);
          if (result.data.redirect_url) {
            window.history.replaceState(null, '', result.data.redirect_url);
          }
        } else {
          currentStore.setSaveStatus('error');
        }
      });
    }).catch(function() {
      useArticleStore.getState().setSaveStatus('error');
    }).finally(function() {
      isSavingRef.current = false;
    });
  }, [csrfToken, ensureDraftExists]);

  var scheduleAutosave = useCallback(function() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, DEBOUNCE_MS);
  }, [save]);

  var saveNow = useCallback(function() {
    if (timerRef.current) clearTimeout(timerRef.current);
    save();
  }, [save]);

  useEffect(function() {
    return function() {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { scheduleAutosave, saveNow };
}
